import { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Chip,
} from "@mui/material";

import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Title,
  Link as LinkIcon,
  Undo,
  Redo,
  Highlight,
  SendOutlined,
  ScheduleSendOutlined,
} from "@mui/icons-material";

import { useToast } from "../../../shared/toastMessages/toastProvider";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import HighlightExt from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";

import TimeSelector from "./features/timeselector";
import MultiSearchSelector from "./features/mailingList";
import ColorPickerButton from "./features/colorPicker";
import ImageInsertButton from "./features/imageDialog";
import SpamHighlight, {
  setSpamHighlightPhrases,
} from "./features/spamHighlight";
import { theme } from "../../../../configs/theme";

import postApiData from "../../../../hooks/apiRequests/postsRequest";
import getApiData from "../../../../hooks/apiRequests/getRequest";
import { containsSpam, normalizeSpamText } from "../../../../hooks/spamCheck";
import { useNavigate } from "react-router-dom";

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "15px",
  boxShadow: "1px 5px 5px 1px rgba(0,0,0,0.12)",
  p: { xs: 2, md: 3 },
};

/** Ensure plain-text settings still render with line breaks. */
const toDisplayHtml = (html) => {
  if (!html || !String(html).trim()) return "";
  const trimmed = String(html).trim();
  if (/<[a-z][\s\S]*>/i.test(trimmed)) return trimmed;
  return trimmed
    .split(/\n/)
    .map((line) => `<div>${line || "<br>"}</div>`)
    .join("");
};

const settingsHtmlSx = {
  px: 2,
  py: 1.5,
  fontSize: theme.typography.body1.fontSize,
  lineHeight: 1.45,
  color: theme.palette.text.primary,
  "& img": {
    maxWidth: "100%",
    height: "auto",
    display: "inline-block",
    verticalAlign: "middle",
  },
  "& a": { color: "#1155cc" },
  "& div, & p": { margin: 0, lineHeight: 1.45 },
};

const ToolbarButton = ({ title, onClick, active = false, children }) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        borderRadius: "8px",
        bgcolor: active ? "rgba(255,0,0,0.08)" : "transparent",
        color: active ? "primary.main" : "text.secondary",
        "&:hover": {
          bgcolor: active ? "rgba(255,0,0,0.12)" : "action.hover",
        },
      }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

const CreateEmail = () => {
  const [title, setTitle] = useState("");
  const [spamData, setSpamData] = useState([]);
  const [groups, setGroups] = useState([]);
  const [releaseTime, setReleaseTime] = useState(null);
  const [greeting, setGreeting] = useState("");
  const [signoff, setSignoff] = useState("");
  const [titleError, setTitleError] = useState("");
  const [groupsError, setGroupsError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [blockedSpamWords, setBlockedSpamWords] = useState([]);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        blockquote: {
          HTMLAttributes: {
            class: "email-blockquote",
          },
        },
      }),
      Underline,
      Link.configure({ openOnClick: false }),
      Image.configure({
        allowBase64: true,
        HTMLAttributes: {
          style: "max-width:100%;height:auto;",
        },
      }),
      TextStyle,
      Color,
      HighlightExt,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      SpamHighlight,
    ],
    content: "<p></p>",
  });

  // Keep in-editor spam highlights in sync with the phrase list.
  useEffect(() => {
    setSpamHighlightPhrases(editor, spamData);
  }, [editor, spamData]);

  // Keep toolbar active states (colour, quote, etc.) in sync with selection.
  const [, setEditorTick] = useState(0);
  useEffect(() => {
    if (!editor) return undefined;
    const refresh = () => setEditorTick((tick) => tick + 1);
    editor.on("selectionUpdate", refresh);
    editor.on("transaction", refresh);
    return () => {
      editor.off("selectionUpdate", refresh);
      editor.off("transaction", refresh);
    };
  }, [editor]);

  useEffect(() => {
    const fetchSpam = async () => {
      try {
        const res = await getApiData("/Spam/Phrases");
        const rows = Array.isArray(res?.data) ? res.data : [];
        setSpamData(
          rows
            .map((item) =>
              normalizeSpamText(
                typeof item === "string"
                  ? item
                  : item?.phrase ?? item?.phraseText ?? item?.text ?? ""
              )
            )
            .filter(Boolean)
        );
      } catch (err) {
        console.error("Error fetching spam phrases:", err);
        showToast("Failed to load spam phrases.", "error");
      }
    };
    fetchSpam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getApiData("/Settings");
        const settings = response?.data || [];

        const getValue = (key) =>
          settings.find((s) => s.name === key)?.config_value || "";
        setGreeting(getValue("welcome_messgae"));
        setSignoff(getValue("sign_off_message"));
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast("Failed to load settings", "error");
      }
    };

    fetchData();
  }, [showToast]);

  const stripHtml = (html) => {
    if (!html) return "";
    try {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      const text = tmp.textContent || tmp.innerText || "";
      return normalizeSpamText(text);
    } catch (e) {
      return normalizeSpamText(html.replace(/<[^>]+>/g, " "));
    }
  };

  const sendEmail = async (body) => {
    try {
      setSubmitting(true);
      const response = await postApiData("/Create/Email", body);
      if (response?.status === 200) {
        navigate(`/Portal/Emails/Manage`);
        const scheduled = Boolean(response?.data?.scheduled);
        const release = response?.data?.release_time
          ? new Date(response.data.release_time)
          : null;
        if (scheduled && release) {
          showToast(
            `Email scheduled for ${release.toLocaleString()}`,
            "success"
          );
        } else {
          showToast("Email sent successfully", "success");
        }
      } else {
        const message =
          response?.data?.message ||
          response?.data?.error ||
          "Could not create email.";
        showToast(message, "error");
      }
    } catch (error) {
      console.error("Error processing Add request:", error);
      showToast("Failed to create email.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const getFullPlainText = () => {
    const greetingHtml = toDisplayHtml(greeting);
    const signoffHtml = toDisplayHtml(signoff);
    const middleHtml = editor?.getHTML?.() || "";
    const bodyHtml = [greetingHtml, middleHtml, signoffHtml]
      .filter(Boolean)
      .join("");
    const bodyText = stripHtml(bodyHtml);
    const scanText = normalizeSpamText(
      [title, bodyText].filter(Boolean).join(" ")
    );
    return {
      bodyHtml,
      plainText: bodyText,
      scanText,
    };
  };

  // Keep the blocked-words banner in sync while the user edits.
  useEffect(() => {
    if (!editor) return undefined;

    const refreshBlockedWords = () => {
      const { scanText } = getFullPlainText();
      setBlockedSpamWords(containsSpam(scanText, spamData));
    };

    refreshBlockedWords();
    editor.on("update", refreshBlockedWords);
    return () => {
      editor.off("update", refreshBlockedWords);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, spamData, greeting, signoff, title]);

  const addLink = () => {
    const url = prompt("Enter URL:");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    let hasError = false;

    if (!trimmedTitle) {
      setTitleError("Please enter an email title.");
      showToast("Please enter an email title.", "error");
      hasError = true;
    } else {
      setTitleError("");
    }

    if (!groups.length) {
      setGroupsError("Please select at least one mailing list.");
      showToast("Please select at least one mailing list.", "error");
      hasError = true;
    } else {
      setGroupsError("");
    }

    if (hasError) return;

    const { bodyHtml, plainText, scanText } = getFullPlainText();

    const spamHits = containsSpam(scanText, spamData);
    if (spamHits.length > 0) {
      setBlockedSpamWords(spamHits);
      showToast(
        `Email rejected. Spam phrases detected: ${spamHits.join(", ")}`,
        "error"
      );
      return;
    }

    setBlockedSpamWords([]);

    const payload = {
      id: 0,
      title: trimmedTitle,
      body: bodyHtml,
      basic_body: plainText,
      groups,
      time_created: new Date().toISOString(),
    };

    if (releaseTime) {
      payload.release_time = releaseTime.toISOString();
    }

    await sendEmail(payload);
  };

  if (!editor) return null;

  const isScheduled = Boolean(releaseTime);

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
      <Stack spacing={0.5} sx={{ py: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "black" }}>
          Create Email
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Write your campaign, choose mailing lists, and send or schedule delivery.
        </Typography>
      </Stack>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems="stretch"
      >
        <Paper elevation={0} sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
            Email title
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. March product update"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError && e.target.value.trim()) {
                setTitleError("");
              }
            }}
            error={Boolean(titleError)}
            helperText={titleError || " "}
            sx={{ mb: 1 }}
          />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
            Email content
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Greeting and sign-off come from General Settings and are kept as saved (including images and spacing). Write the middle of the email below.
          </Typography>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "12px",
              overflow: "hidden",
              bgcolor: "#fff",
              "&:focus-within": {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}22`,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
                px: 1,
                py: 0.75,
                bgcolor: "#fafafa",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <ToolbarButton
                title="Bold"
                active={editor.isActive("bold")}
                onClick={() => editor.chain().focus().toggleBold().run()}
              >
                <FormatBold fontSize="small" />
              </ToolbarButton>
              <ToolbarButton
                title="Italic"
                active={editor.isActive("italic")}
                onClick={() => editor.chain().focus().toggleItalic().run()}
              >
                <FormatItalic fontSize="small" />
              </ToolbarButton>
              <ToolbarButton
                title="Underline"
                active={editor.isActive("underline")}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
              >
                <FormatUnderlined fontSize="small" />
              </ToolbarButton>
              <ToolbarButton
                title="Strikethrough"
                active={editor.isActive("strike")}
                onClick={() => editor.chain().focus().toggleStrike().run()}
              >
                <StrikethroughS fontSize="small" />
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <ToolbarButton
                title="Bullet List"
                active={editor.isActive("bulletList")}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
              >
                <FormatListBulleted fontSize="small" />
              </ToolbarButton>
              <ToolbarButton
                title="Numbered List"
                active={editor.isActive("orderedList")}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
              >
                <FormatListNumbered fontSize="small" />
              </ToolbarButton>
              <ToolbarButton
                title="Blockquote"
                active={editor.isActive("blockquote")}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
              >
                <FormatQuote fontSize="small" />
              </ToolbarButton>
              <ToolbarButton
                title="Heading"
                active={editor.isActive("heading", { level: 2 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              >
                <Title fontSize="small" />
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <ColorPickerButton editor={editor} theme={theme} />
              <ToolbarButton
                title="Highlight"
                active={editor.isActive("highlight")}
                onClick={() => editor.chain().focus().toggleHighlight().run()}
              >
                <Highlight fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Add Link" onClick={addLink}>
                <LinkIcon fontSize="small" />
              </ToolbarButton>
              <ImageInsertButton
                editor={editor}
                theme={theme}
                showToast={showToast}
              />

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <ToolbarButton title="Undo" onClick={() => editor.chain().focus().undo().run()}>
                <Undo fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Redo" onClick={() => editor.chain().focus().redo().run()}>
                <Redo fontSize="small" />
              </ToolbarButton>
            </Box>

            <Box
              sx={{
                minHeight: 320,
                bgcolor: "#fff",
                "& .tiptap": {
                  minHeight: 160,
                  outline: "none",
                  px: 2,
                  py: 1.5,
                  fontFamily: theme.typography.body1.fontFamily,
                  fontSize: theme.typography.body1.fontSize,
                  lineHeight: 1.45,
                  color: theme.palette.text.primary,
                },
                "& .tiptap p": {
                  margin: "0 0 2px",
                },
                "& .tiptap p:last-child": {
                  marginBottom: 0,
                },
                "& .tiptap h1, & .tiptap h2, & .tiptap h3": {
                  margin: "10px 0 4px",
                  fontWeight: 600,
                },
                "& .tiptap blockquote": {
                  margin: "10px 0",
                  padding: "10px 14px",
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  bgcolor: "#fafafa",
                  borderRadius: "0 10px 10px 0",
                  color: "text.secondary",
                  fontStyle: "italic",
                },
                "& .tiptap blockquote p": {
                  margin: 0,
                },
                "& .tiptap img": {
                  maxWidth: "100%",
                  height: "auto",
                  display: "block",
                  margin: "8px 0",
                  borderRadius: "8px",
                },
                "& .tiptap br": {
                  lineHeight: 1.45,
                },
                "& .spam-word-hit": {
                  backgroundColor: "rgba(211, 47, 47, 0.2)",
                  borderBottom: "2px solid #d32f2f",
                  borderRadius: "3px",
                  boxShadow: "inset 0 0 0 1px rgba(211, 47, 47, 0.25)",
                  padding: "0 1px",
                },
              }}
            >
              {greeting ? (
                <Box
                  sx={{
                    ...settingsHtmlSx,
                    borderBottom: "1px dashed",
                    borderColor: "divider",
                    bgcolor: "#fafafa",
                  }}
                  dangerouslySetInnerHTML={{ __html: toDisplayHtml(greeting) }}
                />
              ) : null}
              <EditorContent editor={editor} />
              {signoff ? (
                <Box
                  sx={{
                    ...settingsHtmlSx,
                    borderTop: "1px dashed",
                    borderColor: "divider",
                    bgcolor: "#fafafa",
                  }}
                  dangerouslySetInnerHTML={{ __html: toDisplayHtml(signoff) }}
                />
              ) : null}
            </Box>
          </Box>

          {blockedSpamWords.length > 0 ? (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                borderRadius: "12px",
                alignItems: "flex-start",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                Blocked words detected
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.25 }}>
                This email cannot be sent until the following blocked words or
                phrases are removed:
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {blockedSpamWords.map((word) => (
                  <Chip
                    key={word}
                    label={word}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ borderRadius: "8px", fontWeight: 600 }}
                  />
                ))}
              </Stack>
            </Alert>
          ) : null}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            width: { xs: "100%", lg: 360 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignSelf: { lg: "flex-start" },
            position: { lg: "sticky" },
            top: { lg: 16 },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Email settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose when to send and who should receive it.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2.5} sx={{ flex: 1 }}>
            <TimeSelector value={releaseTime} onChange={setReleaseTime} />
            <Divider />
            <MultiSearchSelector
              value={groups}
              onChange={(vals) => {
                setGroups(vals);
                if (groupsError && vals.length > 0) {
                  setGroupsError("");
                }
              }}
              error={groupsError}
            />
          </Stack>

          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {blockedSpamWords.length > 0 ? (
              <Alert
                severity="error"
                sx={{ mb: 1.5, borderRadius: "10px" }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                  Remove blocked words before sending:
                </Typography>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {blockedSpamWords.map((word) => (
                    <Chip
                      key={`sidebar-${word}`}
                      label={word}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ borderRadius: "8px", fontWeight: 600 }}
                    />
                  ))}
                </Stack>
              </Alert>
            ) : null}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={handleSubmit}
              disabled={submitting || blockedSpamWords.length > 0}
              theme={theme}
              startIcon={
                isScheduled ? <ScheduleSendOutlined /> : <SendOutlined />
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                py: 1.25,
              }}
            >
              {submitting
                ? "Submitting..."
                : isScheduled
                  ? "Schedule Email"
                  : "Send Email"}
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, textAlign: "center" }}
            >
              {blockedSpamWords.length > 0
                ? "Sending is blocked until listed words are removed."
                : isScheduled
                  ? "This campaign will send at the selected time."
                  : "This campaign will send as soon as you submit."}
            </Typography>
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};

export default CreateEmail;
